import { motion } from "framer-motion";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LeaderboardTable } from "./LeaderboardTable";
import {
  LEADERBOARD_LIMIT,
  getLeaderboardMetricLabel
} from "../game/leaderboard";
import type { RunSummary } from "../game/types";
import type { FeedbackPromptRunThreshold } from "../lib/feedbackPrompt";
import {
  fetchFullGlobalLeaderboard,
  fetchGlobalLeaderboard,
  fetchRankedLeaderboardEntry,
  type LeaderboardEntry,
  type LeaderboardSnapshot
} from "../lib/leaderboard";
import type { Theme } from "../theme";
import {
  calculateLeaderboardPreviewLimitFromRowBottoms,
  buildLeaderboardRows,
  estimateLeaderboardEntryRank,
  extendLeaderboardRowBottoms,
  findRankedLeaderboardEntry,
  isLeaderboardEntryVisibleInSlice,
  getLeaderboardErrorMessage,
  shouldUseProvisionalLeaderboardEntry
} from "./leaderboardTableModel";

type GameOverModalProps = {
  open: boolean;
  summary: RunSummary | null;
  theme: Theme;
  leaderboardEnabled: boolean;
  currentUserId: string | null;
  currentProfileName: string;
  latestScoreEntry: LeaderboardEntry | null;
  highlightedScoreEntryId: string | null;
  leaderboardRefreshKey: number;
  isSavingScore: boolean;
  leaderboardSaveError: string | null;
  leaderboardSaveNotice: string | null;
  feedbackPromptThreshold: FeedbackPromptRunThreshold | null;
  onDismissFeedbackPrompt: () => void;
  onOpenFeedbackPrompt: () => void;
  onRestart: () => void;
  onMainMenu: () => void;
};

const GAME_OVER_LEADERBOARD_METRIC = "score";
const GAME_OVER_LEADERBOARD_PERIOD = "all-time";
const FULL_LEADERBOARD_PAGE_SIZE = 100;

function formatScore(score: number): string {
  return score.toLocaleString();
}

function getStatValueSize(value: string): "default" | "compact" | "tight" {
  if (value.length >= 8) {
    return "tight";
  }

  if (value.length >= 6) {
    return "compact";
  }

  return "default";
}

export function GameOverModal({
  open,
  summary,
  theme,
  leaderboardEnabled,
  currentUserId,
  currentProfileName,
  latestScoreEntry,
  highlightedScoreEntryId,
  leaderboardRefreshKey,
  isSavingScore,
  leaderboardSaveError,
  leaderboardSaveNotice,
  feedbackPromptThreshold,
  onDismissFeedbackPrompt,
  onOpenFeedbackPrompt,
  onRestart,
  onMainMenu
}: GameOverModalProps) {
  const [previewSnapshot, setPreviewSnapshot] = useState<LeaderboardSnapshot>({
    entries: [],
    highlightedEntry: null
  });
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewErrorMessage, setPreviewErrorMessage] = useState<string | null>(null);
  const [previewRetryNonce, setPreviewRetryNonce] = useState(0);
  const [previewLimit, setPreviewLimit] = useState(LEADERBOARD_LIMIT);
  const [isExpanded, setIsExpanded] = useState(false);
  const [fullEntries, setFullEntries] = useState<LeaderboardEntry[] | null>(null);
  const [isLoadingFullLeaderboard, setIsLoadingFullLeaderboard] = useState(false);
  const [fullErrorMessage, setFullErrorMessage] = useState<string | null>(null);
  const [fullRetryNonce, setFullRetryNonce] = useState(0);
  const [localLatestScoreCreatedAt, setLocalLatestScoreCreatedAt] = useState<string | null>(null);
  const [rankedLatestScoreEntry, setRankedLatestScoreEntry] = useState<ReturnType<
    typeof findRankedLeaderboardEntry
  >>(null);
  const leaderboardViewportRef = useRef<HTMLDivElement | null>(null);
  const showMeta =
    isSavingScore || leaderboardSaveError !== null || leaderboardSaveNotice !== null;
  const fallbackLatestScoreEntry = useMemo<LeaderboardEntry | null>(() => {
    if (
      !open ||
      summary === null ||
      localLatestScoreCreatedAt === null ||
      !shouldUseProvisionalLeaderboardEntry(isSavingScore, leaderboardSaveError)
    ) {
      return null;
    }

    return {
      id: `local-score-${localLatestScoreCreatedAt}`,
      userId: currentUserId ?? "local-preview-user",
      playerName: currentProfileName.trim().length > 0 ? currentProfileName : "You",
      metricType: GAME_OVER_LEADERBOARD_METRIC,
      metricValue: summary.score.value,
      createdAt: localLatestScoreCreatedAt
    };
  }, [
    currentProfileName,
    currentUserId,
    isSavingScore,
    leaderboardSaveError,
    localLatestScoreCreatedAt,
    open,
    summary
  ]);
  const rankableLatestScoreEntry = latestScoreEntry ?? fallbackLatestScoreEntry;
  const provisionalPreviewHighlightedEntry = useMemo(
    () =>
      rankableLatestScoreEntry === null
        ? null
        : {
            ...rankableLatestScoreEntry,
            rank: estimateLeaderboardEntryRank(previewSnapshot.entries, rankableLatestScoreEntry)
          },
    [previewSnapshot.entries, rankableLatestScoreEntry]
  );
  const effectivePreviewHighlightedEntry = useMemo(
    () =>
      previewSnapshot.highlightedEntry ??
      rankedLatestScoreEntry ??
      findRankedLeaderboardEntry(
        previewSnapshot.entries,
        rankableLatestScoreEntry?.id ?? highlightedScoreEntryId
      ) ??
      provisionalPreviewHighlightedEntry,
    [
      provisionalPreviewHighlightedEntry,
      rankableLatestScoreEntry?.id,
      highlightedScoreEntryId,
      previewSnapshot.entries,
      previewSnapshot.highlightedEntry,
      rankedLatestScoreEntry
    ]
  );
  const effectiveExpandedHighlightedEntry = useMemo(
    () =>
      fullEntries === null
        ? effectivePreviewHighlightedEntry
        : findRankedLeaderboardEntry(fullEntries, highlightedScoreEntryId) ??
          effectivePreviewHighlightedEntry,
    [effectivePreviewHighlightedEntry, fullEntries, highlightedScoreEntryId]
  );
  const shouldAppendDedicatedPreviewRow = useMemo(
    () =>
      effectivePreviewHighlightedEntry !== null &&
      !isLeaderboardEntryVisibleInSlice(
        previewSnapshot.entries,
        effectivePreviewHighlightedEntry.id,
        previewLimit
      ),
    [effectivePreviewHighlightedEntry, previewLimit, previewSnapshot.entries]
  );
  const shouldAppendDedicatedExpandedRow = useMemo(
    () =>
      fullEntries !== null &&
      effectiveExpandedHighlightedEntry !== null &&
      !isLeaderboardEntryVisibleInSlice(
        fullEntries,
        effectiveExpandedHighlightedEntry.id,
        Math.max(fullEntries.length, 1)
      ),
    [effectiveExpandedHighlightedEntry, fullEntries]
  );

  useEffect(() => {
    if (!open || summary === null) {
      setPreviewSnapshot({
        entries: [],
        highlightedEntry: null
      });
      setIsLoadingPreview(false);
      setPreviewErrorMessage(null);
      setPreviewLimit(LEADERBOARD_LIMIT);
      setLocalLatestScoreCreatedAt(null);
      setRankedLatestScoreEntry(null);
      setIsExpanded(false);
      setFullEntries(null);
      setIsLoadingFullLeaderboard(false);
      setFullErrorMessage(null);
      return;
    }

    setPreviewLimit(LEADERBOARD_LIMIT);
    setLocalLatestScoreCreatedAt(new Date().toISOString());
    setRankedLatestScoreEntry(null);
    setIsExpanded(false);
    setFullEntries(null);
    setIsLoadingFullLeaderboard(false);
    setFullErrorMessage(null);
  }, [open, summary]);

  useEffect(() => {
    setRankedLatestScoreEntry(null);
  }, [highlightedScoreEntryId, latestScoreEntry?.id, localLatestScoreCreatedAt, leaderboardRefreshKey]);

  useEffect(() => {
    if (!open || summary === null) {
      return;
    }

    if (!leaderboardEnabled) {
      setPreviewSnapshot({
        entries: [],
        highlightedEntry: null
      });
      setIsLoadingPreview(false);
      setPreviewErrorMessage("The public beta leaderboard is temporarily unavailable.");
      return;
    }

    let cancelled = false;
    setIsLoadingPreview(true);
    setPreviewErrorMessage(null);

    void (async () => {
      try {
        const nextSnapshot = await fetchGlobalLeaderboard(
          GAME_OVER_LEADERBOARD_METRIC,
          GAME_OVER_LEADERBOARD_PERIOD,
          highlightedScoreEntryId,
          LEADERBOARD_LIMIT
        );

        if (cancelled) {
          return;
        }

        setPreviewSnapshot(nextSnapshot);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setPreviewSnapshot({
          entries: [],
          highlightedEntry: null
        });
        setPreviewErrorMessage(getLeaderboardErrorMessage(error, "Could not load leaderboard."));
      } finally {
        if (!cancelled) {
          setIsLoadingPreview(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    highlightedScoreEntryId,
    leaderboardEnabled,
    leaderboardRefreshKey,
    open,
    previewRetryNonce,
    summary
  ]);

  useEffect(() => {
    if (!open || summary === null || !leaderboardEnabled || !isExpanded || fullEntries !== null) {
      return;
    }

    let cancelled = false;
    setIsLoadingFullLeaderboard(true);
    setFullErrorMessage(null);

    void (async () => {
      try {
        const nextEntries = await fetchFullGlobalLeaderboard(
          GAME_OVER_LEADERBOARD_METRIC,
          GAME_OVER_LEADERBOARD_PERIOD,
          FULL_LEADERBOARD_PAGE_SIZE
        );

        if (cancelled) {
          return;
        }

        setFullEntries(nextEntries);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setFullErrorMessage(getLeaderboardErrorMessage(error, "Could not load leaderboard."));
      } finally {
        if (!cancelled) {
          setIsLoadingFullLeaderboard(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fullEntries, fullRetryNonce, isExpanded, leaderboardEnabled, open, summary]);

  useEffect(() => {
    if (
      !open ||
      summary === null ||
      !leaderboardEnabled ||
      rankableLatestScoreEntry === null ||
      previewSnapshot.highlightedEntry !== null
    ) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const rankedEntry = await fetchRankedLeaderboardEntry(
          rankableLatestScoreEntry,
          GAME_OVER_LEADERBOARD_PERIOD
        );

        if (!cancelled) {
          setRankedLatestScoreEntry(rankedEntry);
        }
      } catch {
        if (!cancelled) {
          setRankedLatestScoreEntry(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    leaderboardEnabled,
    open,
    previewSnapshot.highlightedEntry,
    rankableLatestScoreEntry,
    summary
  ]);

  useLayoutEffect(() => {
    if (
      !open ||
      summary === null ||
      isExpanded ||
      isLoadingPreview ||
      previewErrorMessage !== null ||
      leaderboardViewportRef.current === null
    ) {
      return;
    }

    const viewport = leaderboardViewportRef.current;
    const syncPreviewLimit = () => {
      const renderedRows = Array.from(
        viewport.querySelectorAll<HTMLElement>(".leaderboard-row")
      );
      const table = viewport.querySelector<HTMLOListElement>(".leaderboard-table");
      const shell = viewport.parentElement;
      const shellHeader =
        shell?.querySelector<HTMLElement>(".game-over-leaderboard-shell-header") ?? null;
      const tableHead =
        shell?.querySelector<HTMLElement>(".leaderboard-table-head") ?? null;
      const minPreviewLimit = shouldAppendDedicatedPreviewRow ? 2 : 1;
      const maxPreviewLimit = Math.min(
        LEADERBOARD_LIMIT,
        previewSnapshot.entries.length + (shouldAppendDedicatedPreviewRow ? 1 : 0)
      );
      const shellStyles = shell === null ? null : getComputedStyle(shell);
      const shellPaddingTop =
        shellStyles === null ? 0 : Number.parseFloat(shellStyles.paddingTop || "0");
      const shellPaddingBottom =
        shellStyles === null ? 0 : Number.parseFloat(shellStyles.paddingBottom || "0");
      const shellGap =
        shellStyles === null
          ? 0
          : Number.parseFloat(shellStyles.rowGap || shellStyles.gap || "0");
      const shellDerivedViewportHeight =
        shell === null
          ? 0
          : Math.max(
              0,
              shell.clientHeight -
                shellPaddingTop -
                shellPaddingBottom -
                (shellHeader?.offsetHeight ?? 0) -
                (tableHead?.offsetHeight ?? 0) -
                shellGap * 2
            );
      const currentClientHeight = Math.max(viewport.clientHeight, shellDerivedViewportHeight);
      const viewportTop = viewport.getBoundingClientRect().top;
      const rowGap =
        table === null ? 0 : Number.parseFloat(getComputedStyle(table).rowGap || "0");
      const rowBottoms = renderedRows.map(
        (row) => row.getBoundingClientRect().bottom - viewportTop
      );
      const measuredRowHeight = renderedRows[0]?.getBoundingClientRect().height ?? 0;
      const lastRenderedRow = renderedRows[renderedRows.length - 1];
      const dedicatedRowHeight = shouldAppendDedicatedPreviewRow
        ? lastRenderedRow?.getBoundingClientRect().height
        : undefined;

      if (renderedRows.length === 0) {
        return;
      }

      const projectedTopRowBottoms = extendLeaderboardRowBottoms(
        shouldAppendDedicatedPreviewRow ? rowBottoms.slice(0, -1) : rowBottoms,
        measuredRowHeight,
        rowGap,
        shouldAppendDedicatedPreviewRow ? Math.max(maxPreviewLimit - 1, 0) : maxPreviewLimit
      );
      const nextPreviewLimit = Math.min(
        maxPreviewLimit,
        Math.max(
          minPreviewLimit,
          shouldAppendDedicatedPreviewRow
            ? calculateLeaderboardPreviewLimitFromRowBottoms(
                currentClientHeight,
                projectedTopRowBottoms,
                {
                  maxVisibleRows: LEADERBOARD_LIMIT,
                  dedicatedTrailingRowHeight: dedicatedRowHeight,
                  rowGap
                }
              )
            : calculateLeaderboardPreviewLimitFromRowBottoms(
                currentClientHeight,
                projectedTopRowBottoms,
                {
                  maxVisibleRows: LEADERBOARD_LIMIT
                }
              )
        )
      );

      setPreviewLimit((currentLimit) =>
        currentLimit === nextPreviewLimit ? currentLimit : nextPreviewLimit
      );
    };

    syncPreviewLimit();
    const syncPreviewLimitOnNextFrame = window.requestAnimationFrame(() => {
      syncPreviewLimit();
    });

    if (typeof ResizeObserver === "function") {
      const resizeObserver = new ResizeObserver(() => {
        syncPreviewLimit();
      });
      const mutationObserver =
        typeof MutationObserver === "function"
          ? new MutationObserver(() => {
              syncPreviewLimit();
            })
          : null;

      resizeObserver.observe(viewport);
      const firstRow = viewport.querySelector<HTMLElement>(".leaderboard-row");
      if (firstRow !== null) {
        resizeObserver.observe(firstRow);
      }
      mutationObserver?.observe(viewport, {
        childList: true,
        subtree: true
      });

      return () => {
        window.cancelAnimationFrame(syncPreviewLimitOnNextFrame);
        resizeObserver.disconnect();
        mutationObserver?.disconnect();
      };
    }

    window.addEventListener("resize", syncPreviewLimit);
    return () => {
      window.cancelAnimationFrame(syncPreviewLimitOnNextFrame);
      window.removeEventListener("resize", syncPreviewLimit);
    };
  }, [
    isExpanded,
    isLoadingPreview,
    open,
    previewErrorMessage,
    previewSnapshot.entries.length,
    previewSnapshot.highlightedEntry?.id,
    effectivePreviewHighlightedEntry,
    previewLimit,
    shouldAppendDedicatedPreviewRow,
    summary
  ]);

  useEffect(() => {
    if (!isExpanded || fullEntries === null || leaderboardViewportRef.current === null) {
      return;
    }

    const viewport = leaderboardViewportRef.current;
    const highlightedEntryId = effectiveExpandedHighlightedEntry?.id ?? null;
    const animationFrameId = window.requestAnimationFrame(() => {
      if (highlightedEntryId === null) {
        viewport.scrollTop = 0;
        return;
      }

      const matchingRow = Array.from(
        viewport.querySelectorAll<HTMLElement>("[data-entry-id]")
      ).find((row) => row.dataset.entryId === highlightedEntryId);

      if (matchingRow === undefined) {
        viewport.scrollTop = 0;
        return;
      }

      viewport.scrollTop = Math.max(
        0,
        matchingRow.offsetTop - (viewport.clientHeight - matchingRow.offsetHeight) / 2
      );
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [effectiveExpandedHighlightedEntry?.id, fullEntries, isExpanded]);

  const metrics = [
    {
      label: "Score",
      value: formatScore(summary?.score.value ?? 0),
      valueSize: getStatValueSize(formatScore(summary?.score.value ?? 0)),
      isPersonalBest: summary?.score.isPersonalBest ?? false,
      badgeLabel: summary?.score.isPersonalBest ? "BEST" : null,
      primary: false
    },
    {
      label: "Round",
      value: String(summary?.round.value ?? 0),
      valueSize: getStatValueSize(String(summary?.round.value ?? 0)),
      isPersonalBest: summary?.round.isPersonalBest ?? false,
      badgeLabel: summary?.round.isPersonalBest ? "BEST" : null,
      primary: false
    },
    {
      label: "Max Flow",
      value: String(summary?.maxFlow.value ?? 0),
      valueSize: getStatValueSize(String(summary?.maxFlow.value ?? 0)),
      isPersonalBest: summary?.maxFlow.isPersonalBest ?? false,
      badgeLabel: summary?.maxFlow.isPersonalBest ? "BEST" : null,
      primary: false
    }
  ];

  const previewRows = useMemo(
    () =>
      buildLeaderboardRows({
        entries: previewSnapshot.entries,
        highlightedEntry: effectivePreviewHighlightedEntry,
        currentUserId,
        currentProfileName,
        limit: shouldAppendDedicatedPreviewRow ? Math.max(1, previewLimit - 1) : previewLimit,
        allowChangeNameButton: false,
        highlightedEntryMode: shouldAppendDedicatedPreviewRow ? "always" : "outside-top-only"
      }),
    [
      currentProfileName,
      currentUserId,
      effectivePreviewHighlightedEntry,
      previewLimit,
      previewSnapshot.entries,
      previewSnapshot.highlightedEntry,
      shouldAppendDedicatedPreviewRow
    ]
  );
  const expandedRows = useMemo(() => {
    if (fullEntries === null) {
      return [];
    }

    return buildLeaderboardRows({
      entries: fullEntries,
      highlightedEntry: effectiveExpandedHighlightedEntry,
      currentUserId,
      currentProfileName,
      limit: shouldAppendDedicatedExpandedRow
        ? Math.max(fullEntries.length - 1, 1)
        : Math.max(fullEntries.length, 1),
      allowChangeNameButton: false,
      highlightedEntryMode: shouldAppendDedicatedExpandedRow ? "always" : "outside-top-only"
    });
  }, [
    currentProfileName,
    currentUserId,
    effectiveExpandedHighlightedEntry,
    fullEntries,
    previewSnapshot.highlightedEntry,
    shouldAppendDedicatedExpandedRow
  ]);
  const rows = isExpanded && fullEntries !== null ? expandedRows : previewRows;
  const metricLabel = getLeaderboardMetricLabel(GAME_OVER_LEADERBOARD_METRIC);
  const canExpandLeaderboard =
    leaderboardEnabled &&
    !isExpanded &&
    !isLoadingPreview &&
    previewErrorMessage === null &&
    previewSnapshot.entries.length > 0;
  const leaderboardTransitionKey = `game-over-leaderboard-${
    isExpanded ? "expanded" : "preview"
  }-${previewLimit}-${fullEntries?.length ?? previewSnapshot.entries.length}-${leaderboardRefreshKey}`;
  const tableIsLoading = isExpanded ? isLoadingFullLeaderboard : isLoadingPreview;
  const tableErrorMessage = isExpanded ? fullErrorMessage : previewErrorMessage;
  const tableRetryHandler =
    tableErrorMessage === null
      ? undefined
      : isExpanded
        ? () => {
            setFullEntries(null);
            setFullErrorMessage(null);
            setFullRetryNonce((currentValue) => currentValue + 1);
          }
        : leaderboardEnabled
          ? () => {
              setPreviewErrorMessage(null);
              setPreviewRetryNonce((currentValue) => currentValue + 1);
            }
          : undefined;
  const feedbackPromptTitle =
    feedbackPromptThreshold === 1 ? "How did that first run feel?" : "Still with me after a few runs?";
  const feedbackPromptBody =
    feedbackPromptThreshold === 1
      ? "Tell me where the beta felt sharp, confusing, or unfair while the run is still fresh."
      : "A quick note now is more valuable than a polished review later. Tell me what is landing and what is dragging.";

  if (!open || summary === null) {
    return null;
  }

  return (
    <motion.div
      className="game-over-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Game Over"
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <div className={`game-over-card panel panel-${theme}`}>
        <h2>Game Over</h2>

        <dl className="game-over-summary">
          {metrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              className={`game-over-stat${metric.primary ? " game-over-stat-primary" : ""}${
                metric.primary && metric.isPersonalBest ? " game-over-stat-record" : ""
              }`}
              initial={metric.primary ? { scale: 0.97, opacity: 0.84 } : false}
              animate={
                metric.primary
                  ? {
                      scale: [1, 1.025, 1],
                      opacity: 1,
                      boxShadow: [
                        "inset 0 0 0 1px rgba(0, 229, 255, 0.08), 0 0 0 rgba(0, 229, 255, 0)",
                        "inset 0 0 0 1px rgba(0, 229, 255, 0.16), 0 0 28px rgba(0, 229, 255, 0.28)",
                        "inset 0 0 0 1px rgba(0, 229, 255, 0.08), 0 0 0 rgba(0, 229, 255, 0)"
                      ]
                    }
                  : { scale: 1, opacity: 1 }
              }
              transition={{
                duration: metric.primary ? 0.28 : 0.2,
                delay: index * 0.04,
                ease: "easeOut"
              }}
            >
              <dt className="game-over-stat-label">{metric.label}</dt>
              <dd
                className={`game-over-stat-value${
                  metric.valueSize === "default"
                    ? ""
                    : metric.valueSize === "compact"
                      ? " game-over-stat-value-compact"
                      : " game-over-stat-value-tight"
                }`}
              >
                {metric.value}
              </dd>
              <div className="game-over-stat-badge-slot">
                {metric.badgeLabel !== null ? (
                  <span className="game-over-badge">{metric.badgeLabel}</span>
                ) : null}
              </div>
            </motion.div>
          ))}
        </dl>

        {showMeta ? (
          <div className="game-over-meta">
            {isSavingScore ? (
              <p className="game-over-entry-copy">
                Syncing your latest run to the global leaderboard...
              </p>
            ) : null}

            {leaderboardSaveNotice !== null ? (
              <p className="game-over-note" role="status">
                {leaderboardSaveNotice}
              </p>
            ) : null}

            {leaderboardSaveError !== null ? (
              <p className="game-over-error" role="alert">
                {leaderboardSaveError}
              </p>
            ) : null}
          </div>
        ) : null}

        <div
          className={`game-over-leaderboard-region${
            isExpanded ? " game-over-leaderboard-region-expanded" : ""
          }`}
        >
          <div
            className={`leaderboard-table-shell game-over-leaderboard-shell${
              canExpandLeaderboard ? " game-over-leaderboard-shell-interactive" : ""
            }${isExpanded ? " game-over-leaderboard-shell-expanded" : ""}`}
            role={canExpandLeaderboard ? "button" : undefined}
            tabIndex={canExpandLeaderboard ? 0 : undefined}
            aria-expanded={canExpandLeaderboard ? false : undefined}
            aria-label={canExpandLeaderboard ? "Expand full leaderboard" : undefined}
            onClick={canExpandLeaderboard ? () => setIsExpanded(true) : undefined}
            onKeyDown={
              canExpandLeaderboard
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setIsExpanded(true);
                    }
                  }
                : undefined
            }
          >
            <div className="game-over-leaderboard-shell-header">
              <h3 className="game-over-leaderboard-title">Global Scoreboard</h3>
              {!isExpanded && canExpandLeaderboard ? (
                <span className="game-over-leaderboard-affordance">Tap to browse full ranking</span>
              ) : null}
            </div>

            <LeaderboardTable
              metricLabel={metricLabel}
              rows={rows}
              isLoading={tableIsLoading}
              errorMessage={tableErrorMessage}
              transitionKey={leaderboardTransitionKey}
              onRetry={tableRetryHandler}
              loadingMessage={
                isExpanded ? "Loading full ranking..." : "Loading global leaderboard..."
              }
              headClassName="game-over-leaderboard-head"
              viewportClassName={`game-over-leaderboard-viewport${
                isExpanded ? " game-over-leaderboard-viewport-expanded" : ""
              }`}
              statusClassName="game-over-leaderboard-status"
              rowClassName="game-over-leaderboard-row"
              viewportRef={leaderboardViewportRef}
              rowAnimationDelayStep={isExpanded ? 0.02 : 0.04}
            />
          </div>
        </div>

        <div className="game-over-footer">
          {feedbackPromptThreshold !== null ? (
            <div className="game-over-feedback-panel">
              <div className="game-over-feedback-copy">
                <h3>{feedbackPromptTitle}</h3>
                <p>{feedbackPromptBody}</p>
              </div>
              <div className="game-over-feedback-actions">
                <button
                  type="button"
                  className="restart-button restart-button-secondary"
                  onClick={onOpenFeedbackPrompt}
                >
                  Share Feedback
                </button>
                <button
                  type="button"
                  className="game-over-link-button"
                  onClick={onDismissFeedbackPrompt}
                >
                  Later
                </button>
              </div>
            </div>
          ) : null}
          <div className="game-over-action-row">
            <button
              type="button"
              className="restart-button restart-button-primary"
              onClick={onRestart}
            >
              PLAY AGAIN
            </button>
          </div>
          <button type="button" className="game-over-link-button" onClick={onMainMenu}>
            Main Menu
          </button>
        </div>
      </div>
    </motion.div>
  );
}
